import { useState, useCallback, useEffect, useRef } from 'react';
import { trpc } from '@/trpc/client';
import { POLLING_INTERVALS, STALE_TIMES } from '@/lib/constants';
import { useLocationId } from './useLocationId';

export interface ContactListFilters {
  locationId?: string;
  status?: 'lead' | 'customer' | 'inactive';
  hasActiveLeasing?: boolean;
  source?: string;
  search?: string;
  tags?: string[];
  limit?: number;
  offset?: number;
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'leadScore';
  sortOrder?: 'asc' | 'desc';
}

const PAGE_SIZE = 50;

export function useContactList(filters: ContactListFilters = {}) {
  const locationId = useLocationId();
  const [offset, setOffset] = useState(0);
  const prevFiltersRef = useRef('');

  const queryInput = {
    ...filters,
    locationId: filters.locationId ?? locationId,
    limit: filters.limit ?? PAGE_SIZE,
    offset,
    sortBy: filters.sortBy ?? 'updatedAt',
    sortOrder: filters.sortOrder ?? 'desc',
  };

  const query = trpc.contact.list.useQuery(queryInput, {
    refetchInterval: POLLING_INTERVALS.CONTACTS,
    staleTime: STALE_TIMES.FAST,
  });

  const pageContacts = query.data && 'contacts' in query.data ? query.data.contacts : [];
  const total = query.data && 'total' in query.data ? query.data.total : 0;

  // Accumulated contacts across pages
  const [accumulated, setAccumulated] = useState(pageContacts);

  // Reset when filters change
  const filtersKey = JSON.stringify({
    locationId: filters.locationId ?? locationId,
    status: filters.status,
    source: filters.source,
    search: filters.search,
    tags: filters.tags,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    hasActiveLeasing: filters.hasActiveLeasing,
  });

  useEffect(() => {
    if (prevFiltersRef.current !== filtersKey) {
      prevFiltersRef.current = filtersKey;
      setOffset(0);
      setAccumulated([]);
    }
  }, [filtersKey]);

  // Accumulate results when data changes
  useEffect(() => {
    if (!query.data || !('contacts' in query.data)) return;
    const newPage = query.data.contacts;
    if (offset === 0) {
      setAccumulated(newPage);
    } else {
      setAccumulated(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const freshContacts = newPage.filter(c => !existingIds.has(c.id));
        return [...prev, ...freshContacts];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, offset]);

  const hasMore = accumulated.length < total;

  const loadMore = useCallback(() => {
    if (hasMore && !query.isFetching) {
      setOffset(prev => prev + PAGE_SIZE);
    }
  }, [hasMore, query.isFetching]);

  return {
    ...query,
    data: accumulated,
    total,
    hasMore,
    loadMore,
    isFetchingMore: query.isFetching && offset > 0,
  };
}
